import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE_PATH } from 'routes';
import { createPassword } from 'modules/session/actions';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import Button from 'components/UI/Button';
import SuccessModal from 'components/FeedbackModals/SuccessModal';
import ErrorModal from 'components/FeedbackModals/ErrorModal';
import InputPassword from 'components/UI/InputPassword';
import { validatePassword } from 'utils/validatePassword';
import PasswordRuleList from 'components/PasswordRuleList';
import { translate } from 'utils/translate';

function ResetPassword({ literals }) {
  const { isApp } = useSelector((state) => state.app);
  const [newPassword, setNewPassword] = useState('');
  const [repeatNewPassword, setRepeatNewPassword] = useState('');
  const [sending, setSending] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState(null);
  const { search } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = new URLSearchParams(search).get('token');
    if (!token) {
      navigate(ROUTE_PATH.LOGIN);
    }
  }, [navigate, search]);

  const createPasswordHandler = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSending(true);
    const token = new URLSearchParams(search).get('token');
    createPassword(token, newPassword)
      .then((success) => {
        if (success) {
          setShowSuccessModal(true);
        } else {
          setError(true);
        }
      })
      .catch((err) => {
        let newError = literals.errorDescription;
        if (err.statusCode === 401) {
          newError = literals.errorInvalidToken;
        } else {
          const errorCode = err?.data?.error?.password?.code || '';
          switch (errorCode) {
            case 'equal_error':
              newError = literals.errorEqualLast;
              break;
            case 'length_error': {
              newError = literals.errorLength;
              const errorMessage = err?.data?.error?.password?.message || '';
              const matches = errorMessage.match(/\d+/g);
              if (matches.length === 2) {
                const minLength = parseInt(matches[0], 10) || 0;
                const maxLength = parseInt(matches[1], 10) || 0;
                if (minLength && maxLength) {
                  newError = translate(literals.errorLengthNumber, { minLength, maxLength });
                }
              }
              break;
            }
            default:
              break;
          }
        }
        setError(newError);
      })
      .finally(() => {
        setSending(false);
      });
  };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    navigate(ROUTE_PATH.LOGIN);
  };

  const validation = validatePassword(newPassword, repeatNewPassword);

  return (
    <>
      <Layout
        onBack={isApp ? true : null}
        rightMenu={isApp}
        fullHeight
      >
        <AppShell variant='narrow'>
          <div className='full-height-body'>
            <h1 className='mb-4 mt-0'>{literals.title}</h1>
            <form id='reset-password-form' onSubmit={createPasswordHandler}>
              <InputPassword
                name='newPassword'
                className='mt-6'
                placeholder={literals.createPassword}
                value={newPassword}
                autoComplete='new-password'
                onChange={setNewPassword}
                required
              />
              <InputPassword
                name='repeatNewPassword'
                className='my-6'
                placeholder={literals.newPassword}
                value={repeatNewPassword}
                autoComplete='new-password'
                onChange={setRepeatNewPassword}
                required
              />
            </form>
            <p className='mt-6'>{literals.passwordRules.title}</p>
            <PasswordRuleList validation={validation} />
          </div>
          <div className='full-height-footer buttons'>
            <Button
              disabled={!validation.success}
              loading={sending}
              isSubmitBtn
              form='reset-password-form'
              text={literals.common.continue}
            />
          </div>
        </AppShell>
      </Layout>
      {showSuccessModal && (
        <SuccessModal
          description={literals.successDescription}
          onCloseNavigateRoute={ROUTE_PATH.LOGIN}
          onClose={handleCloseSuccessModal}
        >
          <Button
            className='mt-4'
            text={literals.common.continue}
            onClick={handleCloseSuccessModal}
          />
        </SuccessModal>
      )}
      {error && (
        <ErrorModal description={error} onClose={() => setError(null)} />
      )}
    </>
  );
}

ResetPassword.propTypes = {
  literals: PropTypes.object.isRequired,
};

/* *************************************** */
/* ********       CONTAINER       ******** */
/* *************************************** */

function mapStateToProps(state) {
  return {
    literals: {
      ...state.i18n.literals.auth.resetPassword,
      common: {
        ...state.i18n.literals.common,
      },
    },
  };
}

export default connect(mapStateToProps)(ResetPassword);
